import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import { MenuItemStyled } from "@entrilia/shared/components/MenuItemStyled.tsx";
import { Grid, Select, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionField, ReportField } from "../../../../../../../../../api/types/svTypes.ts";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors.ts";
import type { MeasureValidReturnType } from "../../../../../utilities/isConfigurationValid.ts";
import { isMeasureConfigurationValid } from "../../../../../utilities/isConfigurationValid.ts";

interface Props {
  measure: ReportField;
  fields: DimensionField[];
  conditions: ConditionField[];
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  readonly: boolean;
}

export default function CashFlowDateFieldMenuItem({ measure, fields, conditions, saveChanges, readonly }: Props) {
  const dimensions = useSelector(selectDimensions);

  const measureValidity = useMemo(
    () => isMeasureConfigurationValid(conditions, fields, dimensions, measure),
    [conditions, fields, dimensions, measure]
  );

  if (!measureValidity.valid || measure.config.type !== FieldConfigurationType.Irr) {
    return null;
  }

  return (
    <CashFlowDateField
      measure={measure}
      measureValidity={measureValidity}
      saveChanges={saveChanges}
      disabled={readonly}
    />
  );
}

interface CashFlowDateFieldProps {
  measure: ReportField;
  measureValidity: MeasureValidReturnType;
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  disabled: boolean;
}

function CashFlowDateField({ measure, measureValidity, saveChanges, disabled }: CashFlowDateFieldProps) {
  const dimensions = useSelector(selectDimensions);
  const possibleDimensions = useMemo(() => dimensions.filter((d) => d.options?.asDateReference === true), [dimensions]);
  const selectedField = useMemo(
    () =>
      possibleDimensions.find(
        (f) => f.name === (measure.config.periodDateField ?? measureValidity.referenceDateField?.name)
      ),
    [measure.config.periodDateField, measureValidity.referenceDateField, possibleDimensions]
  );

  const handleChange = useCallback(
    (fieldName: string) => {
      saveChanges({ periodDateField: fieldName }, false);
    },
    [saveChanges]
  );

  useEffect(() => {
    if (disabled || measure.config.periodDateField || !selectedField) {
      return;
    }

    saveChanges({ periodDateField: selectedField.name }, false);
  }, [disabled, measure.config.periodDateField, selectedField, saveChanges]);

  return (
    <Grid container sx={{ alignItems: "center", width: "100%" }}>
      <Typography>Cash Flow Date</Typography>
      <HorizontalFill />
      <Select
        value={selectedField?.name}
        sx={{ width: 208, ".MuiSelect-select": { py: 0.7, alignContent: "center" } }}
        onChange={(evt) => handleChange(evt.target.value)}
        disabled={disabled}
      >
        {possibleDimensions.map((value) => (
          <MenuItemStyled key={value.name} value={value.name}>
            <Stack direction="row" sx={{ flex: 1 }}>
              <Typography variant="caption">{value.caption}</Typography>
            </Stack>
          </MenuItemStyled>
        ))}
      </Select>
    </Grid>
  );
}
